import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const ICStatus = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Independent Contractor Status" 
        description="Understanding your IC relationship with BlueLux"
      >
        <section className="space-y-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <p className="text-lg font-medium text-foreground mb-3">
              You are an independent contractor, not an employee, agent, joint venturer, or partner of BlueLux.
            </p>
            <p className="text-muted-foreground">
              This is a non-exclusive, independent contractor relationship. BlueLux does not guarantee a minimum 
              number of jobs or exclusive access to routes.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">What This Means for You</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">You Control Your Business</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Control your own schedule, methods, and means of providing services</li>
                  <li>Operate an independently established business</li>
                  <li>Can provide similar services to other clients</li>
                  <li>Not restricted from engaging in business with other clients (subject to BlueLux confidentiality requirements)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">You Are Solely Responsible For</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>All federal, state, and local taxes, including self-employment taxes</li>
                  <li>All tax withholdings and statutory obligations</li>
                  <li>Your business expenses and tools (vehicle, fuel, test kit, smartphone, PPE)</li>
                  <li>Any permits or licenses required to perform services</li>
                  <li>Maintaining all necessary licenses, permits, and business presence as required by law</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Scope of Services</h2>
            <p className="text-muted-foreground mb-3">
              You will provide pool cleaning, maintenance, and related services for BlueLux customers. Assignment 
              availability and acceptance will occur through the BlueLux Technician App.
            </p>
            <p className="text-muted-foreground mb-3">You agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Follow the BlueLux Service Standards</li>
              <li>Use the BlueLux Technician App to log jobs, readings, checklists, and required photos</li>
              <li>Maintain professional conduct and appearance while servicing customers</li>
              <li>Deliver all work in a safe and reliable manner</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">What BlueLux Provides</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Pool chemicals (covered by $5 service fee deduction)</li>
              <li>Access to customer routes through the Technician App</li>
              <li>Service training and support</li>
              <li>Platform support and administrative tools</li>
              <li>Optional equipment purchase/lease at fair market value</li>
              <li>Optional vehicle lease ($15/day fee)</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">What You Provide</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Reliable vehicle for transportation to customer sites (or lease from BlueLux)</li>
              <li>Your own tools and equipment (or purchase/lease from BlueLux at fair market value)</li>
              <li>Test kit for water chemistry</li>
              <li>Smartphone capable of running the BlueLux Technician App</li>
              <li>Personal protective equipment (PPE)</li>
              <li>Professional labor and service expertise</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Tax Responsibilities</h2>
            <div className="bg-muted/50 border border-border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                As an independent contractor, you will receive an annual <strong>Form 1099-NEC</strong> for all 
                compensation paid by BlueLux.
              </p>
              <p className="text-muted-foreground mb-4">
                You are responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Quarterly estimated tax payments (IRS Form 1040-ES)</li>
                <li>Self-employment tax (Social Security & Medicare)</li>
                <li>Federal and state income taxes</li>
                <li>Maintaining proper business records for tax purposes</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                BlueLux recommends consulting with a tax professional to ensure compliance with all tax obligations.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Business Expense Deductions</h2>
            <p className="text-muted-foreground mb-3">
              As an independent contractor, you may be eligible to deduct business expenses, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Vehicle expenses (mileage or actual expenses method)</li>
              <li>Fuel costs</li>
              <li>Vehicle maintenance and repairs</li>
              <li>Auto insurance</li>
              <li>Phone/data plan (business portion)</li>
              <li>Tools and equipment</li>
              <li>Supplies not provided by BlueLux</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4 italic">
              Consult with a tax professional for guidance on deductible expenses and record-keeping requirements.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Agreement Term & Termination</h2>
            <p className="text-muted-foreground mb-3">
              This independent contractor relationship continues until terminated by either party.
            </p>
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-medium">Termination Rights</h3>
                <p className="text-muted-foreground">
                  Either BlueLux or the Contractor may terminate this agreement at any time, with or without cause, 
                  by providing written notice to the other party.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium">Upon Termination</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Contractor must return all BlueLux property, materials, and customer information</li>
                  <li>Confidentiality obligations continue indefinitely</li>
                  <li>Final payment for completed services will be processed according to normal payment schedule</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-medium mb-3">Key Differences: Employee vs. Independent Contractor</h3>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <p className="font-medium text-foreground mb-2">As an Independent Contractor:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>✓ Control your own schedule</li>
                  <li>✓ Can work for other companies</li>
                  <li>✓ Responsible for your own taxes</li>
                  <li>✓ Provide your own equipment</li>
                  <li>✓ Set your own methods</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-foreground mb-2">Unlike an Employee:</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>✗ No guaranteed hours or income</li>
                  <li>✗ No employer-paid benefits</li>
                  <li>✗ No workers' compensation coverage</li>
                  <li>✗ No unemployment insurance</li>
                  <li>✗ No payroll tax withholding</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default ICStatus;
